import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCartOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

import ProductImagePlaceholder from './ProductImagePlaceholder';
import QuantityStepper from './QuantityStepper';
import { CONTACT_WHATSAPP_NUMBER } from '../../../config';

export const formatMoney = (value, currency = 'PEN') =>
  new Intl.NumberFormat(currency === 'USD' ? 'en-US' : 'es-PE', { style: 'currency', currency }).format(value || 0);

// Etiqueta de los productos en oferta o en remate (pestaña "Ofertas", ver OffersPage).
export const OFFER_LABELS = {
  oferta: { label: 'Oferta', color: 'error' },
  remate: { label: 'Remate', color: 'warning' },
};

// Tarjeta de un producto del catalogo: permite elegir cantidad y agregarlo al carrito.
// Se reutiliza en el catalogo y en las vitrinas de la home (tendencia, mas vendido, novedades).
// `readOnly` (lista de precios del distribuidor, solo de consulta) oculta la cantidad, "Agregar",
// "Comprar por WhatsApp" y "Envío express" (solo muestra el stock), y no enlaza a la ficha publica.
export default function ProductCard({ product, currency, onAddToCart, readOnly }) {
  const [quantity, setQuantity] = useState(1);
  const priceField = currency === 'USD' ? 'price_usd' : 'price_pen';
  const regularPrice = product[currency === 'USD' ? 'price_usd_regular' : 'price_pen_regular'];
  const offer = OFFER_LABELS[product.offer_type];
  const outOfStock = product.stock_quantity <= 0;

  const handleAdd = () => {
    onAddToCart(product, quantity);
    setQuantity(1);
  };

  const whatsAppMessage = `Hola, quisiera consultar por el producto "${product.name}".`;
  const whatsAppHref = `https://web.whatsapp.com/send?phone=${CONTACT_WHATSAPP_NUMBER}&text=${encodeURIComponent(whatsAppMessage)}`;

  const image = product.image_url ? (
    <Box
      component="img"
      src={product.image_url}
      alt={product.name}
      sx={{ height: 160, width: '100%', objectFit: 'contain', display: 'block', bgcolor: 'background.paper' }}
    />
  ) : (
    <ProductImagePlaceholder />
  );

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {offer && (
        <Chip
          size="small"
          color={offer.color}
          label={offer.label}
          sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1, fontWeight: 700 }}
        />
      )}
      {readOnly ? (
        image
      ) : (
        <RouterLink to={`/productos/${product.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
          {image}
        </RouterLink>
      )}

      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Stack spacing={0.5} sx={{ flexGrow: 1 }}>
          {readOnly ? (
            <Typography variant="subtitle1">{product.name}</Typography>
          ) : (
            <Typography
              variant="subtitle1"
              component={RouterLink}
              to={`/productos/${product.id}`}
              sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              {product.name}
            </Typography>
          )}
          <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
            <Chip
              size="small"
              color={outOfStock ? 'default' : 'success'}
              label={outOfStock ? 'Sin stock' : `Stock: ${product.stock_quantity}`}
            />
            {product.express_delivery && !readOnly && (
              <Chip
                size="small"
                variant="outlined"
                color="primary"
                icon={<LocalShippingOutlinedIcon fontSize="small" />}
                label="Envío express"
              />
            )}
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', mt: 2 }}>
          <Typography variant="h6" color={offer ? `${offer.color}.main` : 'text.primary'}>
            {formatMoney(product[priceField], currency)}
          </Typography>
          {regularPrice != null && (
            <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
              {formatMoney(regularPrice, currency)}
            </Typography>
          )}
        </Stack>

        {!readOnly && (
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 2 }}>
            <QuantityStepper value={quantity} onChange={setQuantity} disabled={outOfStock} />

            <Button
              fullWidth
              variant="contained"
              startIcon={<AddShoppingCartIcon />}
              onClick={handleAdd}
              disabled={outOfStock}
              sx={{ ml: 1 }}
            >
              Agregar
            </Button>
          </Stack>
        )}

        {!readOnly && (
          <Button
            fullWidth
            size="small"
            variant="outlined"
            color="success"
            startIcon={<WhatsAppIcon />}
            component="a"
            href={whatsAppHref}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ mt: 1 }}
          >
            Comprar por WhatsApp
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

ProductCard.propTypes = {
  product: PropTypes.object.isRequired,
  currency: PropTypes.oneOf(['PEN', 'USD']),
  onAddToCart: PropTypes.func,
  readOnly: PropTypes.bool,
};

ProductCard.defaultProps = {
  currency: 'PEN',
  onAddToCart: undefined,
  readOnly: false,
};
